import React, { useState, Suspense, FC, useEffect } from 'react';
import { get, map } from 'lodash';
import { TabButtonStyled, WeldDetailItemTabStyled } from './styled';
import { weldDetailItemTabList } from './tab-data';
import { WeldDetailItemProps, WeldDetailItemTab, WeldDetailItemTabType } from './types';
import { useLingui } from '@lingui/react';
import { ErrorBoundary, ActionContent, SuspenseLoader, WorkPackage3dViewer } from '@bechtel/client-common';
import {
  Spinner,
  SpinnerSize,
  Separator,
  SeparatorType,
  Button,
  Box,
  ButtonSize,
  useSideSheetState,
} from '@bechtel/gds';
import { WeldSummary } from './summary';
import { WeldItemDetails } from './details';
import { WeldPhotos } from './photos';
import { WeldDetailItemDocuments } from './documents';
import { t } from '@lingui/macro';

export const WeldDetailItem: FC<WeldDetailItemProps> = ({ weld, fetchMore, loading }) => {
  const { isoDocument, workPackage, type, tag } = weld;
  const totalActivities = get(weld, 'milestonesActivityHistory.total', 0);
  const totalPages = Math.ceil(totalActivities / 10);
  const activityCursor = get(weld, 'milestonesActivityHistory.cursor', 1);
  const [selectedTab, setSelectedTab] = useState<WeldDetailItemTabType>(WeldDetailItemTabType.SUMMARY);
  const { i18n } = useLingui();
  const [photoCount, setPhotoCount] = useState(0);

  const { visible: show3dViewer, open: open3dViewer, close: close3dViewer } = useSideSheetState(false);
  const fetchResult = () => {
    fetchMore({
      variables: {
        paging: {
          cursor: activityCursor.toString(),
          limit: 10,
        },
      },
    });
  };

  useEffect(() => {
    if (activityCursor <= totalPages) {
      if (!loading) {
        fetchResult();
      }
    }
  }, [activityCursor, totalPages]);

  return (
    <>
      <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'space-between' }}>
        <WeldDetailItemTabStyled>
          {map(weldDetailItemTabList, (tabItem: WeldDetailItemTab) => {
            return (
              <TabButtonStyled
                selected={tabItem.value === selectedTab}
                onClick={() => {
                  setSelectedTab(tabItem.value);
                }}
                key={tabItem.value}
              >
                {tabItem.value === WeldDetailItemTabType.PHOTOS && photoCount
                  ? `${i18n._(tabItem.label)} (${photoCount})`
                  : i18n._(tabItem.label)}
              </TabButtonStyled>
            );
          })}
        </WeldDetailItemTabStyled>
        {/* <Box flexShrink={{ sm: '0' }} mr={{ sm: '450' }}>
          <Button size={ButtonSize.SMALL} onClick={open3dViewer}>
            <ActionContent icon="cube" label={t`View 3D Model`} labelSize="300" />
          </Button>
        </Box> */}
      </Box>
      <Separator type={SeparatorType.HORIZONTAL} spacing="000" color="grey.300" />
      {selectedTab == WeldDetailItemTabType.SUMMARY && (
        <ErrorBoundary>
          <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
            <WeldSummary weld={weld} />
          </Suspense>
        </ErrorBoundary>
      )}
      {selectedTab == WeldDetailItemTabType.DETAILS && (
        <ErrorBoundary>
          <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
            <WeldItemDetails weld={weld} />
          </Suspense>
        </ErrorBoundary>
      )}
      {selectedTab == WeldDetailItemTabType.PHOTOS && (
        <ErrorBoundary>
          <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
            <WeldPhotos showPhotoCount={(count) => setPhotoCount(count)} weldCommodity={weld} fetchMore={fetchMore} />
          </Suspense>
        </ErrorBoundary>
      )}
      {selectedTab == WeldDetailItemTabType.DOCUMENTS && (
        <ErrorBoundary>
          <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
            <WeldDetailItemDocuments isoDocument={isoDocument} weld={weld} />
          </Suspense>
        </ErrorBoundary>
      )}

      {show3dViewer && (
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          <WorkPackage3dViewer
            workPackageTag={workPackage?.tag ? workPackage?.tag : ''}
            commodityName={type?.name ? type?.name : ''}
            commodityTag={tag ? tag : ''}
            onClose={close3dViewer}
          />
        </Suspense>
      )}
    </>
  );
};
